import { Box, Typography, StackProps, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import LoaderComponent from 'components/Loader';
import type {
  EChartsOption,
  XAXisComponentOption,
  YAXisComponentOption,
  SeriesOption
} from 'echarts';
import { useTheme } from "@mui/material/styles";
import colors from 'assets/theme/base/colors';
import { SxProps, Theme } from '@mui/system';

interface YAxisSeries {
  name: string;
  values: number[];
  yAxis: 'left' | 'right';
  color?: string;
}

interface DualYAxisLineChartProps {
  title: string;
  isLoading?: boolean;
  data: {
    xAxisLabels: string[];
    series: YAxisSeries[];
  };
  containerProps?: StackProps;
  sx?: SxProps<Theme>;
}

const getGridLeft = (containerWidth: number): string => {
  if (containerWidth < 420) return '18%';
  if (containerWidth < 600) return '15%';
  if (containerWidth < 820) return '12%';
  if (containerWidth < 920) return '9%';
  if (containerWidth < 1260) return '7%';
  return '5%';
};

const DualYAxisLineChartWidget = ({
  title,
  isLoading = false,
  data,
  sx,
  containerProps
}: DualYAxisLineChartProps) => {
  const theme= useTheme();
  const colors = theme.palette;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [gridLeft, setGridLeft] = useState('5%');

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        const calculatedLeft = getGridLeft(containerWidth);
        setGridLeft(calculatedLeft);
        setWindowSize(window.innerWidth);
      }
    }, 200);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const xAxisOptions: XAXisComponentOption[] = [
    {
      type: 'category',
      data: data.xAxisLabels,
      axisLabel: { color: `${colors.text.subtle}` },
      axisLine: { show: true },
      splitLine: { show: false },
      axisTick: { show: false }
    }
  ];

  const yAxisOptions: YAXisComponentOption[] = [
    {
      type: 'value',
    //   name: leftSeries.map(s => s.name).join(', '),
      name: '',
      nameGap: 0,
      position: 'left',
      axisLabel: { color: `${colors.text.subtle}` },
      axisLine: {
        show: true,
        lineStyle: {
          color: `${colors.text.subtle}`
        }
      },
      splitLine: { show: false }
    },
    {
      type: 'value',
    //   name: rightSeries.map(s => s.name).join(', '),
      name: '',
      nameGap: 0,
      position: 'right',
      axisLabel: { color: `${colors.text.subtle}` },
      axisLine: {
        show: true,
        lineStyle: {
          color: `${colors.text.subtle}`
        }
      },
      splitLine: { show: false }
    }
  ];

  const seriesOptions: SeriesOption[] = data?.series?.map((s) => ({
    name: s.name,
    type: 'line',
    yAxisIndex: s.yAxis === 'left' ? 0 : 1,
    data: s.values,
    smooth: false,
    itemStyle: { color: s.color || (s.yAxis === 'left' ? '#ff00b3' : `${colors.boxColor.orange.color}`) }
  }));

  const option: EChartsOption = {
    grid: { left: gridLeft, right: '8%', bottom: '18%' },
    tooltip: { trigger: 'axis' },
    legend: {
        left: 10,
        bottom: 0,
        data: data?.series?.map(s => s.name),
        textStyle: { color: `${colors.text.subtle}` }
    },
    xAxis: xAxisOptions,
    yAxis: yAxisOptions,
    series: seriesOptions
  };

  return (
    <Box sx={{ borderRadius: '5px', backgroundColor: '#003771', ...sx }} p={2}>
      <Box {...containerProps}>
        <Typography variant="widgetTitle" sx={{ color: 'text.main' }}>
          {title}
        </Typography>
        <Box sx={{ width: '100%', height: 350 }} key={windowSize} ref={chartContainerRef}>
          {!isLoading ? (
            <ReactECharts option={option} style={{ width: '100%', height: '100%' }} />
          ) : (
            <LoaderComponent pt={20} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DualYAxisLineChartWidget;